import Controller from '@ember/controller';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import MultipassExternalApi from '@banking/api/services/multipass-external-api';
import { Error } from '@square/dashboard-protos/models/squareup/multipass/external';
import { TwoFactorDetails } from '@square/dashboard-protos/models/squareup/multipass/common';
import focusInput from '@banking/savings-onboarding-app/utils/focus-input';
import type RouterService from '@ember/routing/router-service';

// We know that verification codes will be numeric, but we don't know how long
// they will be for sure.
// Let's assume they will be at least 4 digits (default is currently 6):
const VERIFICATION_REGEX = /^\d{4,}$/;

const INPUT_SELECTOR = 'input#sfs-signup__verify-google-auth';

export default class TwoFactorGoogleAuthController extends Controller {
  @service intl!: IntlService;
  @service multipassExternalApi!: MultipassExternalApi;
  @service router!: RouterService;
  @tracked isLoading = false;
  @tracked showValidation = false;
  @tracked isSendingCode = false;
  @tracked serverError = false;
  @tracked verificationCode = '';

  get clientInvalid() {
    return (
      !this.verificationCode || !VERIFICATION_REGEX.test(this.verificationCode)
    );
  }

  get validation() {
    return {
      isInvalid: this.serverError || this.clientInvalid,
      message: this.intl.t('sfs.signup.twoFactor.googleAuth.validation'),
    };
  }

  @action async submit(event: Event): Promise<void> {
    event.preventDefault();

    if (this.clientInvalid) {
      this.showValidation = true;
      focusInput(INPUT_SELECTOR);
    } else {
      this.showValidation = false;
      this.isLoading = true;
      try {
        const response =
          await this.multipassExternalApi.upgradeSessionTwoFactorRequest({
            googleauth: {
              verificationCode: this.verificationCode,
            },
          } as TwoFactorDetails);
        if (response.error) {
          if (response.error === Error.INVALID_CREDENTIALS) {
            this.serverError = true;
            this.showValidation = true;
            focusInput(INPUT_SELECTOR);
          } else {
            this.router.transitionTo('signup.errored');
          }
        } else {
          this.router.transitionTo('signup.confirm-identity');
        }
      } catch (error) {
        this.send('handleRpcError', error);
      } finally {
        this.isLoading = false;
      }
    }
  }
}
